import React, { Fragment, useEffect, useState } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/footer';
import Scrollbar from '../../components/scrollbar';
import Logo from '../../images/Logo/logo-white-bg.png';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { userQuery } from '../../store/actions/contactActions';
import CustomLoader from '../../components/CustomLoader';
import axios from 'axios';
import './style.scss';

const ContactPage = () => {
    const dispatch = useDispatch();
    const push = useNavigate();
    const [loading, setLoading] = useState(false);
    const [forms, setForms] = useState({ name: '', surname: '', email: '', message: '' });
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [sent, setSent] = useState(false);
    const [socials, setSocials] = useState({});

    useEffect(() => { window.scrollTo(0, 0); }, []);

    useEffect(() => {
        axios.get(process.env.REACT_APP_API_BASE_URL + '/socials')
            .then(r => { if (r.data && r.data[0]) { const { _id, updatedAt, ...links } = r.data[0]; setSocials(links); } })
            .catch(() => {});
    }, []);

    const contactEmail = socials['gmail'] || 'info@deepprabha.com';
    const contactAddress = socials['address'] || 'Ireland';
    const contactPhones = (socials['phones'] || [])
        .flatMap(p => p.split(','))
        .map(p => p.trim())
        .filter(Boolean);

    const INFO_CARDS = [
        {
            icon: '📍',
            title: 'Our Location',
            lines: [contactAddress],
            link: `https://maps.google.com/?q=${encodeURIComponent(contactAddress)}`,
        },
        {
            icon: '✉️',
            title: 'Email Us',
            lines: [contactEmail],
            link: `mailto:${contactEmail}`,
        },
        ...(contactPhones.length > 0 ? [{
            icon: '📞',
            title: 'Call Us',
            lines: contactPhones,
            links: contactPhones.map(ph => `tel:${ph.replace(/\s/g, '')}`),
        }] : []),
        {
            icon: '🕐',
            title: 'Business Hours',
            lines: ['Mon – Fri: 9am – 6pm', 'Sat: 10am – 2pm'],
        },
    ];

    const validate = (data) => {
        const e = {};
        if (!data.name.trim()) e.name = 'First name is required';
        if (!data.surname.trim()) e.surname = 'Last name is required';
        if (!data.email.trim()) e.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(data.email)) e.email = 'Enter a valid email';
        if (!data.message.trim()) e.message = 'Message is required';
        return e;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForms(prev => ({ ...prev, [name]: value }));
        if (touched[name]) {
            const errs = validate({ ...forms, [name]: value });
            setErrors(prev => ({ ...prev, [name]: errs[name] }));
        }
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched(prev => ({ ...prev, [name]: true }));
        const errs = validate(forms);
        setErrors(prev => ({ ...prev, [name]: errs[name] }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const allTouched = { name: true, surname: true, email: true, message: true };
        setTouched(allTouched);
        const errs = validate(forms);
        setErrors(errs);
        if (Object.keys(errs).length > 0) return;
        setLoading(true);
        dispatch(userQuery(forms, push));
        setForms({ name: '', surname: '', email: '', message: '' });
        setLoading(false);
        setSent(true);
    };

    const fieldClass = (name) => {
        if (touched[name] && errors[name]) return 'dp-ct-field has-error';
        if (touched[name] && forms[name] && !errors[name]) return 'dp-ct-field is-valid';
        return 'dp-ct-field';
    };

    return (
        <Fragment>
            <Helmet>
                <title>Contact Us — Deep Prabha Transchem</title>
                <link rel="canonical" href="https://www.deepprabha.com/contact" />
                <meta name="description" content="Contact Deep Prabha Transchem for chemical supply inquiries, bulk orders, pricing, and business collaboration. Based in Mumbai, serving clients across India and 85+ countries." />
                <meta name="keywords" content="contact deep prabha transchem, chemical supplier contact mumbai, bulk chemical inquiry india, chemical trading company contact, chemical supplier phone email" />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://www.deepprabha.com/contact" />
                <meta property="og:title" content="Contact Deep Prabha Transchem" />
                <meta property="og:description" content="Reach out to Deep Prabha Transchem for inquiries, support, or collaboration." />
                <meta property="og:image" content="https://www.deepprabha.com/logo512.png" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Contact Deep Prabha Transchem" />
                <meta name="twitter:image" content="https://www.deepprabha.com/logo512.png" />
            </Helmet>
            <CustomLoader show={loading} />
            <Navbar hclass="wpo-header-style-3" Logo={Logo} />

            <div className="dp-ct">

                {/* ── HERO ── */}
                <div className="dp-ct-hero">
                    <div className="dp-ct-hero__overlay" />
                    <div className="dp-ct-hero__content">
                        <span className="dp-ct-hero__tag">Get In Touch</span>
                        <h1 className="dp-ct-hero__title">Contact Us</h1>
                        <p className="dp-ct-hero__sub">
                            Have a question or need a quote? Our team is ready to help — reach out and we'll respond within 24 hours.
                        </p>
                        <div className="dp-ct-hero__badges">
                            <span>⚡ Fast Response</span>
                            <span>🤝 Friendly Team</span>
                            <span>🌍 Global Reach</span>
                        </div>
                    </div>
                    <div className="dp-ct-hero__wave">
                        <svg viewBox="0 0 1440 70" preserveAspectRatio="none">
                            <path d="M0,35 C360,70 1080,0 1440,35 L1440,70 L0,70 Z" fill="#f4f6fb" />
                        </svg>
                    </div>
                </div>

                {/* ── INFO CARDS ── */}
                <div className="dp-ct-info-strip">
                    <div className="dp-ct-info-strip__inner">
                        {INFO_CARDS.map((card, i) => (
                            <div className="dp-ct-info-card" key={i}>
                                <div className="dp-ct-info-card__icon">{card.icon}</div>
                                <h3 className="dp-ct-info-card__title">{card.title}</h3>
                                {card.links
                                    ? <p className="dp-ct-info-card__line dp-ct-info-card__line--phones">
                                        {card.lines.map((line, j) => (
                                            <React.Fragment key={j}>
                                                <a href={card.links[j]} className="dp-ct-info-card__line--link">{line}</a>
                                                {j < card.lines.length - 1 && ', '}
                                            </React.Fragment>
                                        ))}
                                      </p>
                                    : card.lines.map((line, j) =>
                                        card.link && j === 0
                                            ? <a key={j} href={card.link} className="dp-ct-info-card__line dp-ct-info-card__line--link dp-ct-info-card__line--clamp">{line}</a>
                                            : <p key={j} className="dp-ct-info-card__line dp-ct-info-card__line--clamp">{line}</p>
                                    )
                                }
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── FORM + MAP ── */}
                <div className="dp-ct-body">
                    <div className="dp-ct-layout">

                        {/* FORM */}
                        <div className="dp-ct-form-wrap">
                            <div className="dp-ct-form-header">
                                <span className="dp-ct-form-header__icon">💬</span>
                                <div>
                                    <h2>Send Us a Message</h2>
                                    <p>Fill in the form below and we'll get back to you shortly.</p>
                                </div>
                            </div>

                            {sent ? (
                                <div className="dp-ct-success">
                                    <span>🎉</span>
                                    <h3>Message Sent!</h3>
                                    <p>Thank you for reaching out. We'll be in touch within 24 hours.</p>
                                    <button className="dp-ct-btn" onClick={() => setSent(false)}>Send Another</button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="dp-ct-form" noValidate>
                                    <div className="dp-ct-form__grid">
                                        <div className={fieldClass('name')}>
                                            <label>First Name <span className="dp-ct-req">*</span></label>
                                            <div className="dp-ct-input-wrap">
                                                <span className="dp-ct-input-icon">👤</span>
                                                <input
                                                    type="text" name="name" value={forms.name}
                                                    placeholder="John"
                                                    onChange={handleChange} onBlur={handleBlur}
                                                />
                                            </div>
                                            {touched.name && errors.name && <span className="dp-ct-error">{errors.name}</span>}
                                        </div>

                                        <div className={fieldClass('surname')}>
                                            <label>Last Name <span className="dp-ct-req">*</span></label>
                                            <div className="dp-ct-input-wrap">
                                                <span className="dp-ct-input-icon">👤</span>
                                                <input
                                                    type="text" name="surname" value={forms.surname}
                                                    placeholder="Doe"
                                                    onChange={handleChange} onBlur={handleBlur}
                                                />
                                            </div>
                                            {touched.surname && errors.surname && <span className="dp-ct-error">{errors.surname}</span>}
                                        </div>

                                        <div className={`${fieldClass('email')} dp-ct-field--full`}>
                                            <label>Email Address <span className="dp-ct-req">*</span></label>
                                            <div className="dp-ct-input-wrap">
                                                <span className="dp-ct-input-icon">✉️</span>
                                                <input
                                                    type="email" name="email" value={forms.email}
                                                    placeholder="john@company.com"
                                                    onChange={handleChange} onBlur={handleBlur}
                                                />
                                            </div>
                                            {touched.email && errors.email && <span className="dp-ct-error">{errors.email}</span>}
                                        </div>

                                        <div className={`${fieldClass('message')} dp-ct-field--full`}>
                                            <label>Your Message <span className="dp-ct-req">*</span></label>
                                            <div className="dp-ct-input-wrap dp-ct-input-wrap--textarea">
                                                <textarea
                                                    name="message" value={forms.message}
                                                    placeholder="How can we help you?"
                                                    rows={5}
                                                    onChange={handleChange} onBlur={handleBlur}
                                                />
                                            </div>
                                            {touched.message && errors.message && <span className="dp-ct-error">{errors.message}</span>}
                                        </div>
                                    </div>

                                    <button type="submit" className="dp-ct-submit">
                                        Send Message <span>→</span>
                                    </button>
                                    <p className="dp-ct-disclaimer">
                                        🔒 Your information is secure and will never be shared.
                                    </p>
                                </form>
                            )}
                        </div>

                        {/* RIGHT PANEL */}
                        <div className="dp-ct-side">
                            <div className="dp-ct-side-card">
                                <h3>Why Reach Out?</h3>
                                <ul className="dp-ct-side-list">
                                    <li><span>🧪</span> Product availability & pricing</li>
                                    <li><span>📦</span> Bulk order enquiries</li>
                                    <li><span>🚚</span> Shipping & logistics questions</li>
                                    <li><span>🔬</span> Technical specifications</li>
                                    <li><span>🤝</span> Partnership & distribution</li>
                                    <li><span>📋</span> Documentation requests</li>
                                </ul>
                            </div>

                            <div className="dp-ct-side-card dp-ct-side-card--navy">
                                <h3>Prefer a product request?</h3>
                                <p>Use our dedicated request form to specify product, quantity, and delivery requirements.</p>
                                <a href="/request" className="dp-ct-btn dp-ct-btn--outline">
                                    📩 Submit a Request →
                                </a>
                            </div>
                        </div>

                    </div>
                </div>

            </div>

            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default ContactPage;
